import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { 
    GameDataLayerWrapper,
    GameDataLayerWrapperWithSeparator,
    GameDataLayerContentWrapper,
    InningStateOverride,
    ViewingActiveGameWrapper
} from './GameDataLayer.style';
import menOnBaseText from 'bdata-sns-shared/utils/getAccessibilityText.js';
import COLORS from '@mlb/react-patterns/v4/style/universal.color';
import getTranslation from 'bdata-sns-shared/utils/getTranslation';
import MiniSBGameModes from 'bdata-sns-shared/mini/types/MiniSBGameModes';
import { getBaseOutColors } from 'bdata-sns-shared/store/selectors/getGamedayBaseOutColors';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton';

const { VERTICAL, GAMEDAY } = MiniSBGameModes;
const {
    mlbBlack
} = COLORS;
// Right side of desktop card with inning state / times
// not rendered on mobile
const GameDataLayer = ({
    dictionary,
    linescore,
    basepathsWidth = 23,
    outsWidth,
    mode,
    isActiveGame,
    isLoading
}) => {
    const {
        outs,
        balls,
        strikes,
        offense = {}
    } = linescore;
    const { first, second, third } = offense;
    const basepaths = { first: !!first, second: !!second, third: !!third };
    const outText = outs + ' ' + (outs > 1 ? 'Outs' : 'Out');
    const menOnBase = menOnBaseText(basepaths);
    const translatedBasesAltText = getTranslation(dictionary, menOnBase.dictKeys, menOnBase.text);
    const translatedActiveViewing = getTranslation(dictionary, 'label_viewing', 'Viewing');
    const isGameday = mode === GAMEDAY;
    const isVerticalGame = mode === VERTICAL;
    const { baseColor, outColor } = useSelector(getBaseOutColors);
    const basepathColor = baseColor || mlbBlack;
    const outsColor = outColor || mlbBlack;

    let gameDataDisplay = null;

    if (isLoading) {
        gameDataDisplay = <Skeleton width={32} height={18} margin={8} />;
    } else if (isActiveGame && mode !== VERTICAL) {
        gameDataDisplay = (
            <ViewingActiveGameWrapper>
                {translatedActiveViewing}
            </ViewingActiveGameWrapper>
        );
    } else {
        gameDataDisplay = (
            <GameDataLayerContentWrapper
                isGameday={isGameday}
                $isVerticalGame={isVerticalGame}
            >
                <InningStateOverride
                    outCount={outs}
                    balls={balls}
                    strikes={strikes}
                    first={basepaths.first}
                    second={basepaths.second}
                    third={basepaths.third}
                    basepathsWidth={basepathsWidth}
                    outsWidth={outsWidth}
                    showCount={false}
                    basesAltText={translatedBasesAltText}
                    outsAltText={outText}
                    strokeColor={basepathColor}
                    filledColor={basepathColor}
                    emptyColor='transparent'
                    rounded={false}
                    outsInactiveColor='transparent'
                    outsActiveColor={outsColor}
                    stroke
                    outPadding={3}
                />
            </GameDataLayerContentWrapper>
        );
    }

    const GameDataLayerWrapperType = isActiveGame || isLoading ? GameDataLayerWrapper : GameDataLayerWrapperWithSeparator;
    return (
            <GameDataLayerWrapperType>
                { gameDataDisplay }
            </GameDataLayerWrapperType>
    );
};

GameDataLayer.propTypes = {
    flags: PropTypes.object,
    dictionary: PropTypes.object,
    linescore: PropTypes.object,
    basepathsWidth: PropTypes.number,
    outsWidth: PropTypes.number,
    mode: PropTypes.oneOf(Object.values(MiniSBGameModes)),
    isActiveGame: PropTypes.bool,
    isLoading: PropTypes.bool
};

export default GameDataLayer;
